import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import {
  createResidenceCategory,
  CreateResidenceCategoryRequest,
  CreatedResponse,
} from "../../clients/BackOfficeApi";
import { useNavigate } from "react-router-dom";
import { PlusSquareIcon } from "@chakra-ui/icons";
import { isApiError, isBadRequest, ValidationErrorModel } from "../../utils/apiUtils";
import { getDynamicMessageId } from "../I18n";
import { useIntl } from "react-intl";
import { ErrorList } from "../../elements/ErrorList";
import { ConfirmCloseDirtyDialog } from "./ConfirmCloseDirtyDialog";

export function CreateResidenceCategoryModal() {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [closingDirty, setClosingDirty] = useState(false);
  const [validationError, setValidationError] =
    useState<ValidationErrorModel<CreateResidenceCategoryRequest>>();
  const [error, setError] = useState<string>();
  const [createResidenceCategoryModel, setCreateResidenceCategoryModel] =
    useState<CreateResidenceCategoryRequest>();
  const navigate = useNavigate();
  const intl = useIntl();

  const isDirty = useMemo(
    () => Boolean(createResidenceCategoryModel?.name.trim()),
    [createResidenceCategoryModel?.name]
  );

  const isValid = useMemo(
    () => Boolean(createResidenceCategoryModel?.name.trim()),
    [createResidenceCategoryModel?.name]
  );

  const onCloseDirtyConfirm = useCallback((confirmed: boolean) => {
    setClosingDirty(false);
    if (!confirmed) return;

    setCreateResidenceCategoryModel({ name: "" });
    onClose();
  }, [onClose]);

  const onCloseGuarded = useCallback(() => {
    if (!isDirty) {
      onClose();
    }

    setClosingDirty(isDirty);
  }, [isDirty, onClose]);

  const create = useCallback(() => {
    if (!createResidenceCategoryModel) return;

    setValidationError(undefined);
    setError(undefined);

    createResidenceCategory({
      body: createResidenceCategoryModel,
      throwOnError: true,
    })
      .then((response) => {
        if (response.response.status === 201) {
          const createdResponse = response.data as CreatedResponse;
          navigate(`/residence-categories/${createdResponse.id}`);
          onClose();
        }
      })
      .catch((e) => {
        if (isApiError(e)) {
          const errorId = getDynamicMessageId(e.title ?? "Unknown error");

          setError(intl.formatMessage({ id: errorId, defaultMessage: e.title ?? "Unknown error" }));

          if (isBadRequest(e)) {
            setValidationError(e.errors);
          }
        } else {
          setError(`Fout bij het aanmaken van de woningcategorie: ${e}`);
        }
      });
  }, [createResidenceCategoryModel, navigate, onClose, intl]);

  return (
    <>
      <Button onClick={onOpen}>
        <PlusSquareIcon mr={2} />
        Nieuwe woningcategorie
      </Button>
      <Modal closeOnOverlayClick isOpen={isOpen} onClose={onCloseGuarded}>
        <ModalOverlay />
        <ModalContent onSubmit={create}>
          <ConfirmCloseDirtyDialog isOpen={closingDirty} onConfirm={onCloseDirtyConfirm} />
          <ModalHeader>Nieuwe woningcategorie aanmaken</ModalHeader>
          <ModalCloseButton />
          <ModalBody as="form" onSubmit={(e) => { e.preventDefault(); create(); }}>
            {!!error && <Text color="red.500">{error}</Text>}
            <FormControl>
              <FormLabel>Naam</FormLabel>
              <Input
                isInvalid={!!validationError?.Name}
                onChange={(e) =>
                  setCreateResidenceCategoryModel((c) => ({
                    ...c,
                    name: e.target.value,
                  }))
                }
                placeholder="Voer de naam van de woningcategorie in"
                value={createResidenceCategoryModel?.name ?? ""}
              />
            </FormControl>
            <ErrorList errors={validationError?.Name} />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              isDisabled={!isValid}
              mr={3}
              onClick={create}
            >
              Opslaan
            </Button>
            <Button onClick={onCloseGuarded} variant="ghost">
              Annuleren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
